import React, { useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'reactstrap';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import productService from '../../services/productService';
import { formatPrice } from '../../helpers/utility';

const displayDate = value => new Date(value).toLocaleDateString('fr-FR', { timeZone: 'UTC' });
const number = value => value == null ? null : Number(value);
const PriceTooltip = ({ active, payload }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return <div className="product-price-tooltip"><strong>{displayDate(point.time)} · {point.reference}</strong>
    <div>Achat HT : {point.purchasePrice == null ? 'Non renseigné' : formatPrice(point.purchasePrice)}</div>
    {point.salePrice != null && <div>Vente HT : {formatPrice(point.salePrice)}</div>}
  </div>;
};

export default function ProductPriceChart({ articleId, type, refresh }) {
  const [state, setState] = useState({ loading: true, points: [], error: false });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ loading: true, points: [], error: false });
    productService.priceHistory(articleId, type, controller.signal).then(data => {
      if (!Array.isArray(data)) throw new Error('Historique invalide');
      const points = data.map(point => ({ ...point, time: Date.parse(`${point.date}T00:00:00Z`),
        purchasePrice: number(point.purchasePrice), salePrice: number(point.salePrice),
      })).filter(point => Number.isFinite(point.time));
      if (active) setState({ loading: false, points, error: false });
    }).catch(() => { if (active) setState({ loading: false, points: [], error: true }); });
    return () => { active = false; controller.abort(); };
  }, [articleId, type, refresh, retry]);
  const known = state.points.filter(point => point.purchasePrice != null);
  const first = known[0]?.purchasePrice, last = known[known.length - 1]?.purchasePrice;
  const difference = last == null ? null : last - first;
  const hasPrices = state.points.some(point => point.purchasePrice != null || point.salePrice != null);
  return <section className="product-price-chart" aria-label="Évolution des prix HT">
    <div className="product-price-chart-heading"><div><h2>Évolution des prix HT</h2><small>
      {type === 'RECEPTION' ? 'Toutes les réceptions validées' : 'Tous les BL livrés'} · prix enregistrés par document, en DH
    </small></div>{last != null && <div className="product-price-summary">Dernier achat <strong>{formatPrice(last)}</strong>
      {known.length > 1 && <small>Écart initial : {difference > 0 ? '+' : ''}{formatPrice(difference)}</small>}</div>}</div>
    {state.loading ? <div role="status" className="product-price-placeholder"><Spinner size="sm" /> Chargement de l'évolution…</div>
      : state.error ? <Alert color="warning" className="mb-0">Évolution indisponible. <Button color="link" onClick={() => setRetry(value => value + 1)}>Réessayer</Button></Alert>
        : !hasPrices ? <p className="product-price-placeholder text-muted">Aucun prix enregistré dans les documents validés.</p>
          : <><ResponsiveContainer width="100%" height={155}><LineChart data={state.points} margin={{ top: 5, right: 14, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="time" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={displayDate} minTickGap={40} tick={{ fontSize: 11 }} />
            <YAxis width={55} tick={{ fontSize: 11 }} tickFormatter={value => Number(value).toLocaleString('fr-FR')} />
            <Tooltip content={<PriceTooltip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="linear" dataKey="purchasePrice" name="Achat HT" stroke="#465fff" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} isAnimationActive={false} />
            {type === 'DELIVERY' && <Line type="linear" dataKey="salePrice" name="Vente HT" stroke="#16a085" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} isAnimationActive={false} />}
          </LineChart></ResponsiveContainer><small className="text-muted">Les coûts manquants ne sont pas remplacés par le prix actuel de la fiche dans le graphique.</small></>}
  </section>;
}
